import { KeyboardEvent } from 'react'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import { SearchOutlined } from '@mui/icons-material'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import { colors } from '@mui/material'
import { Preloader } from '../../../common'
import { IADGroup } from '../'
import { MembershipGroupNameDialogButton, MembershipGroupNameDialogView } from '../membership'
import { useGroupSearchable } from '../../../../hooks'
import { EntitiesTypes, getEntityTitle } from '../../../../view/admin/ad-management/EntitiesTabs'

interface IProps {
  isLoading: boolean
  type: EntitiesTypes
  groupsAdded: IADGroup[]
  onGroup: (group: IADGroup) => void
  onSearch: (search: string) => void
}

export const boxName = (type: EntitiesTypes, titleName: string) => {
  switch (type) {
    case EntitiesTypes.omit_measures:
      return 'Omit'
    default:
      return titleName
  }
}

export const EntityGroupsAdded = ({ isLoading, groupsAdded, onGroup, onSearch, type }: IProps) => {
  const titleName = getEntityTitle(type)

  const { searchableText, setSearchableText, isShow, groupDialogDetailTitle, onCancel, setGroupDialogDetailTitle } = useGroupSearchable()
  const renderPreloader = () => (
    <Preloader
      size='sm'
      style={{
        display: 'block',
        position: 'absolute',
        top: 'calc(50% - 30px)',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }}
    />
  )

  const renderHeader = () => {
    const renderSearch = () => {
      const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
          const search = (event.target as HTMLInputElement).value
          onSearch(search.toLowerCase())
        }
      }

      return (
        <TextField
          size='small'
          fullWidth
          placeholder='Search'
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchOutlined />
              </InputAdornment>
            ),
          }}
          inputProps={{
            'data-testid': 'groups-added-search',
          }}
          margin='none'
          onKeyDown={handleKeyDown}
        />
      )
    }

    return (
      <Grid
        container
        alignItems='center'
        sx={{ mb: 2 }}
      >
        <Grid
          item
          xs={7}
        >
          <Typography
            variant='h3'
            sx={{
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            }}
          >
            Added to {boxName(type, titleName)} Groups
          </Typography>
        </Grid>
        <Grid
          item
          xs={5}
        >
          {renderSearch()}
        </Grid>
      </Grid>
    )
  }

  const renderEmptyState = () => <Box sx={{ textAlign: 'center', my: 'auto' }}>No Added {titleName} Groups</Box>

  const handleGroup = (group: IADGroup, isChecked: boolean) => {
    onGroup({ ...group, isChecked })
  }

  const renderGroups = () => (
    <FormGroup>
      {groupsAdded.map((group) => (
        <Card
          key={group.name}
          variant='outlined'
          sx={{ mb: 2 }}
        >
          <FormControlLabel
            labelPlacement='start'
            control={<Checkbox onChange={({ target: { checked } }) => handleGroup(group, checked)} />}
            label={
              <MembershipGroupNameDialogButton
                label={group.name}
                setSearchableText={setSearchableText}
                searchableText={group.name}
              />
            }
            sx={{
              py: 1,
              pl: 2,
              pr: 1,
              mx: 0,
              width: '100%',
              justifyContent: 'space-between',
              borderLeft: `3px solid ${colors.green[500]}`,
            }}
            data-testid={group.name}
          />
        </Card>
      ))}
    </FormGroup>
  )

  return (
    <Card data-testid='groups-added'>
      <CardContent>
        {renderHeader()}
        <FormControl
          fullWidth
          sx={{
            position: 'relative',
            height: 'calc(100vh - 504px)',
            overflowY: 'auto',
          }}
        >
          {isLoading ? renderPreloader() : groupsAdded.length > 0 ? renderGroups() : renderEmptyState()}
        </FormControl>
      </CardContent>
      <MembershipGroupNameDialogView
        isShow={isShow}
        searchableText={searchableText}
        setSearchableText={setSearchableText}
        onCancel={onCancel}
        setTitle={setGroupDialogDetailTitle}
        title={groupDialogDetailTitle}
        from='entities'
      />
    </Card>
  )
}
